import React, {useCallback} from 'react'
import {
  Alerts,
  CommentForm,
  Comments,
  type CommentType,
  RPCDocs,
  RPCs,
  useCurrentUser,
  usePaginationState,
  useRPC,
  useRPCContext,
  useRPCServerEventHandler,
} from '@therms/react-modules'

interface UserSOSCommentsProps {
  userSOSId: string
}

function UserSOSComments({userSOSId}: UserSOSCommentsProps) {
  const {recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords} =
    usePaginationState('usersoscmnt', [])
  const user = useCurrentUser()

  const {
    call,
    data,
    isCalling: loading,
  } = useRPC<
    RPCDocs.UserSos.GetUserSosComments.v1.Args,
    RPCDocs.UserSos.GetUserSosComments.v1.Data
  >(RPCs.UserSos.GetUserSosComments.v1, {
    pagination: {limit: recordsPerPage, skip: skippedRecords},
    userSOSId,
  })

  useRPCServerEventHandler('UserSOSActivationUpdated', (evt, payload) => {
    if (payload.id === userSOSId) call()
  })

  const {rpcService} = useRPCContext()

  const handleSaveComment = useCallback(
    async (values: RPCDocs.UserSos.SaveUserSosComment.v1.Args['comment']) => {
      try {
        await rpcService.UserSos.SaveUserSosComment.v1.call({
          comment: values,
          userSOSId,
        })

        await call()

        Alerts.success(`Comment saved`)
      } catch (err: any) {
        Alerts.error('There was a problem saving the comment.', err.message)

        console.log('handleSaveComment() err', err)
      }
    },
    [userSOSId],
  )

  const handleDeleteComment = useCallback(async ({id}: {id: string}) => {
    try {
      await rpcService.UserSos.DeleteUserSosComment.v1.call({
        commentId: id,
      })

      await call()

      Alerts.success(`Comment Deleted`)
    } catch (err: any) {
      Alerts.error('There was a problem deleting the comment.', err.message)

      console.log('handleSaveComment() err', err)
    }
  }, [])

  return (
    <div className="flex h-full flex-col">
      <CommentForm onSubmit={handleSaveComment} />

      <div className="my-sm border-border border-t" />

      <div className="pb-sm grow overflow-y-auto">
        <Comments
          checkCanEditComment={(comment: CommentType) =>
            user.superAdmin || user.id === comment.createdByUser?.id
          }
          comments={data?.comments}
          commentsPerPage={recordsPerPage}
          loading={loading}
          onCommentsPerPageChange={setRecordsPerPage}
          onEdit={handleSaveComment}
          onDelete={handleDeleteComment}
          onPageClick={setSkippedRecords}
          skippedComments={skippedRecords}
          totalComments={data?.total}
        />
      </div>
    </div>
  )
}

export {UserSOSComments}
